/**
 * /admin/invites — Send personalized cold-outreach onboarding invites
 *
 * One form for two flows:
 *   - "Service Provider" → fires send_provider_invite_email
 *   - "Transporter" → fires send_transporter_invite_email
 *
 * The body is fully canned (with [trust signals, agreement summary, apply
 * link]); admin only fills in recipient name + email + optional personal
 * note ("Hey Sarah, met you at the Carmel show last weekend...").
 *
 * Below the form: log of recent invites sent (deduped by email) so admin
 * can spot-check conversion + avoid resending.
 *
 * Tab: also shows the reminder-cron trigger button so admin can manually
 * fire T+24h/T+72h Stripe Connect nudges for stalled approved providers.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatApiError } from "@/lib/utils-app";
import { PaperPlaneTiltIcon as PaperPlaneTilt, ArrowLeftIcon as ArrowLeft, GraduationCapIcon as GraduationCap, TruckIcon as Truck, BellRingingIcon as BellRinging, CheckCircleIcon as CheckCircle, ClockIcon as Clock } from "@phosphor-icons/react";

export default function AdminInvites() {
  const [kind, setKind] = useState("provider"); // 'provider' | 'transporter'
  const [form, setForm] = useState({
    email: "",
    recipient_name: "",
    inviter_name: "",
    custom_note: "",
  });
  const [sending, setSending] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [reminderResult, setReminderResult] = useState(null);
  const [reminderBusy, setReminderBusy] = useState(false);

  const loadInvites = useCallback(async () => {
    try {
      setLoadingInvites(true);
      const r = await api.get("/admin/onboarding/invites?limit=100");
      setInvites(r.data?.invites || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not load invite history");
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const send = async () => {
    if (!form.email) return toast.error("Recipient email is required");
    setSending(true);
    try {
      const endpoint =
        kind === "provider"
          ? "/admin/onboarding/invite-provider"
          : "/admin/onboarding/invite-transporter";
      await api.post(endpoint, form);
      toast.success(`Invite sent to ${form.email}`);
      setForm({ email: "", recipient_name: "", inviter_name: form.inviter_name, custom_note: "" });
      loadInvites();
    } catch (e) {
      toast.error(formatApiError(e) || "Could not send invite");
    } finally {
      setSending(false);
    }
  };

  const runReminders = async () => {
    setReminderBusy(true);
    setReminderResult(null);
    try {
      const r = await api.post("/admin/onboarding/reminders/run", {});
      setReminderResult(r.data);
      toast.success(
        `Reminders sent — ${r.data.sent_24h} at 24h, ${r.data.sent_72h} at 72h (skipped ${r.data.skipped})`
      );
    } catch (e) {
      toast.error(formatApiError(e) || "Could not run reminders");
    } finally {
      setReminderBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl px-4 py-8" data-testid="admin-invites-page">
        <Link
          to="/admin"
          className="text-[12px] text-[#6B6B6B] hover:text-crimson inline-flex items-center gap-1 mb-3"
        >
          <ArrowLeft size={12} weight="bold" /> Back to admin
        </Link>

        <h1 className="font-spectral text-4xl tracking-tight">Onboarding invites</h1>
        <p className="text-[#6B6B6B] mt-2 leading-relaxed max-w-2xl">
          Send a personalized cold-outreach email to a prospective service
          provider or transporter. The body is canned (trust signals,
          economics, apply link) — you just fill in the contact info and an
          optional personal note.
        </p>

        {/* Tabs */}
        <div className="mt-8 inline-flex bg-[#FAFAFA] border border-[#EAEAEA] rounded-full p-1">
          <button
            type="button"
            onClick={() => setKind("provider")}
            className={`px-4 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors inline-flex items-center gap-1.5 ${
              kind === "provider" ? "bg-ink text-white shadow-sm" : "text-ink hover:text-crimson"
            }`}
            data-testid="tab-provider"
          >
            <GraduationCap size={13} weight="duotone" /> Service Provider
          </button>
          <button
            type="button"
            onClick={() => setKind("transporter")}
            className={`px-4 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors inline-flex items-center gap-1.5 ${
              kind === "transporter" ? "bg-ink text-white shadow-sm" : "text-ink hover:text-crimson"
            }`}
            data-testid="tab-transporter"
          >
            <Truck size={13} weight="duotone" /> Transporter
          </button>
        </div>

        {/* Form */}
        <div className="mt-4 rounded-xl border border-[#EAEAEA] bg-white p-5">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
                Recipient email *
              </Label>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                type="email"
                placeholder="trainer@example.com"
                className="mt-1"
                data-testid="invite-email"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
                Recipient name
              </Label>
              <Input
                value={form.recipient_name}
                onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                placeholder="Sarah"
                className="mt-1"
                data-testid="invite-recipient-name"
              />
            </div>
          </div>

          <div className="mb-4">
            <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
              Your name (signs the email)
            </Label>
            <Input
              value={form.inviter_name}
              onChange={(e) => setForm({ ...form, inviter_name: e.target.value })}
              placeholder="e.g. Marcus, Founder"
              className="mt-1"
              data-testid="invite-inviter-name"
            />
            <p className="text-[11px] text-[#6B6B6B] mt-1">
              Defaults to your admin name if left blank.
            </p>
          </div>

          <div className="mb-4">
            <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
              Personal note (optional)
            </Label>
            <Textarea
              value={form.custom_note}
              onChange={(e) => setForm({ ...form, custom_note: e.target.value })}
              rows={4}
              placeholder="Met you at the Carmel show last weekend — would love to bring your kennel onto the registry..."
              className="mt-1 text-[13px]"
              maxLength={1500}
              data-testid="invite-custom-note"
            />
            <p className="text-[11px] text-[#6B6B6B] mt-1">
              Appears as an italic paragraph above the canned pitch. Max 1500 chars. HTML is escaped.
            </p>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-[11.5px] text-[#6B6B6B]">
              Apply URL the email links to:{" "}
              <code className="bg-[#FAFAFA] px-1.5 py-0.5 rounded">
                {kind === "provider" ? "/become-a-provider" : "/transport/apply"}
              </code>
            </p>
            <Button
              onClick={send}
              disabled={sending || !form.email}
              className="rounded-full bg-crimson hover:bg-ink text-white px-6"
              data-testid="invite-send-btn"
            >
              <PaperPlaneTilt size={13} weight="bold" className="mr-1.5" />
              {sending ? "Sending…" : `Send ${kind === "provider" ? "provider" : "transporter"} invite`}
            </Button>
          </div>
        </div>

        {/* Reminder cron trigger */}
        <div className="mt-8 rounded-xl border border-gold/55 bg-cream/30 p-5" data-testid="reminder-cron-card">
          <div className="flex items-start gap-3">
            <BellRinging size={22} weight="duotone" className="text-gold flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h2 className="font-spectral text-xl tracking-tight">Stripe Connect reminder cron</h2>
              <p className="text-[12.5px] text-[#3F3F3F] mt-1 leading-relaxed">
                Walks every approved provider whose Stripe Connect onboarding isn't
                finished and fires a T+24h or T+72h email. Idempotent —
                running it twice in the same day won't duplicate emails. For
                production, point an external cron at{" "}
                <code className="bg-white px-1.5 py-0.5 rounded text-[11px]">
                  POST /api/admin/onboarding/reminders/run
                </code>{" "}
                every 6 hours.
              </p>
              {reminderResult && (
                <div className="mt-3 text-[12.5px] flex flex-wrap gap-4">
                  <span className="text-[#0F8043]">
                    <CheckCircle size={12} weight="fill" className="inline mr-1" />
                    Sent at 24h: <strong>{reminderResult.sent_24h}</strong>
                  </span>
                  <span className="text-[#0F8043]">
                    <CheckCircle size={12} weight="fill" className="inline mr-1" />
                    Sent at 72h: <strong>{reminderResult.sent_72h}</strong>
                  </span>
                  <span className="text-[#6B6B6B]">
                    <Clock size={12} weight="duotone" className="inline mr-1" />
                    Skipped: <strong>{reminderResult.skipped}</strong>
                  </span>
                </div>
              )}
              <div className="mt-3">
                <Button
                  onClick={runReminders}
                  disabled={reminderBusy}
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  data-testid="run-reminders-btn"
                >
                  {reminderBusy ? "Running…" : "Run reminders now"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Invite log */}
        <div className="mt-8">
          <h2 className="font-spectral text-2xl tracking-tight mb-3">Recent invites</h2>
          {loadingInvites ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : invites.length === 0 ? (
            <p className="text-[#6B6B6B] text-sm">No invites sent yet.</p>
          ) : (
            <div className="rounded-xl border border-[#EAEAEA] bg-white divide-y divide-[#F0F0F0]" data-testid="invite-log">
              {invites.map((inv, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`invite-row-${i}`}>
                  <div className="flex-shrink-0">
                    {inv.kind === "provider" ? (
                      <GraduationCap size={16} weight="duotone" className="text-ink" />
                    ) : (
                      <Truck size={16} weight="duotone" className="text-crimson" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-ink truncate">
                      {inv.recipient_name ? `${inv.recipient_name} · ${inv.email}` : inv.email}
                    </p>
                    <p className="text-[11px] text-[#6B6B6B] mt-0.5">
                      {new Date(inv.sent_at).toLocaleString()}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px] uppercase">
                    {inv.kind}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
